<!--
  «Поля» / «Справочники»: modal with local draft + Cancel/Save actions (mirrors PassportBuilderListsModal UX).
-->
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { cloneDeep, isEqual } from 'lodash'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import { useNotification, VIcons } from '@/shared/ui'
import { useTranslate } from '@/shared/lib'
import PassportBuilderFieldsModalBody from './PassportBuilderFieldsModalBody.vue'

import { isAutoRelationTableField } from '../model/usePalette'
import {
  catalogFieldTypesFromClasses,
  createObjectTypeFieldDraft,
  objectTypeFieldDraftToPayload,
  objectTypeFieldsToInventoryFields,
  objectTypeFieldToDraft,
  primitiveFieldTypesFromClasses,
  useCreatePassportBuilderObjectTypeField,
  useDeletePassportBuilderObjectTypeField,
  useUpdatePassportBuilderObjectTypeField,
  type ObjectTypeFieldsResponse,
  type PassportBuilderObjectTypeFieldDraft,
} from '../model/objectTypeFields'

type PassportFieldsModalMode = 'exclude_list_types' | 'list_types_only'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    title: string
    /** Source fields from builder (applied only on Save). */
    fields: ObjectInventoryField[]
    parentFields: ObjectInventoryField[]
    fieldGroups: ObjectTypeGroup[]
    objectTypeId?: number | string
    objectTypeFieldsResponse: ObjectTypeFieldsResponse
    objectTypeFieldsLoading: boolean
    refetchObjectTypeFields: () => Promise<ObjectTypeFieldsResponse>
    mode?: PassportFieldsModalMode
    /** Merge into builder state and persist to server. close modal only on true. */
    persistFn: (merged: ObjectInventoryField[]) => Promise<boolean>
  }>(),
  { mode: 'exclude_list_types' },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  fieldsSaved: [fields: ObjectInventoryField[]]
}>()

const { addError, addSuccess } = useNotification()
const { trs } = useTranslate()

const draftFields = ref<ObjectInventoryField[]>([])
const snapshot = ref<ObjectInventoryField[]>([])
const objectTypeFieldDrafts = ref<PassportBuilderObjectTypeFieldDraft[]>([])
const objectTypeFieldSnapshot = ref<PassportBuilderObjectTypeFieldDraft[]>([])
const saving = ref(false)

const isOwnFieldsMode = computed(() => props.mode === 'exclude_list_types')
const isCatalogFieldsMode = computed(() => props.mode === 'list_types_only')
const isNewObjectTypeFieldsMode = computed(() => isOwnFieldsMode.value || isCatalogFieldsMode.value)
const objectTypeIdNumber = computed(() => Number(props.objectTypeId) || 0)

const createObjectTypeFieldMutation = useCreatePassportBuilderObjectTypeField(objectTypeIdNumber)
const updateObjectTypeFieldMutation = useUpdatePassportBuilderObjectTypeField(objectTypeIdNumber)
const deleteObjectTypeFieldMutation = useDeletePassportBuilderObjectTypeField(objectTypeIdNumber)

const objectTypeFieldsResponse = computed(() => props.objectTypeFieldsResponse)

const primitiveFieldTypes = computed(() =>
  primitiveFieldTypesFromClasses(objectTypeFieldsResponse.value.fieldClasses),
)
const catalogFieldTypes = computed(() =>
  catalogFieldTypesFromClasses(objectTypeFieldsResponse.value.fieldClasses),
)

const allowedFieldTypeIds = computed(() => {
  const types = isCatalogFieldsMode.value ? catalogFieldTypes.value : primitiveFieldTypes.value
  return new Set(types.map((item) => item.id))
})

const activeFieldTypes = computed(() =>
  isCatalogFieldsMode.value ? catalogFieldTypes.value : primitiveFieldTypes.value,
)

const filteredObjectTypeFields = computed(() =>
  objectTypeFieldsResponse.value.fields.filter((field) => {
    if (allowedFieldTypeIds.value.has(field.field_type)) return true
    if (isCatalogFieldsMode.value) return Number(field.catalog_id) > 0
    return false
  }),
)

function matchesMode(field: ObjectInventoryField) {
  const isLt = field.type === 'list_types'
  if (props.mode === 'list_types_only') return isLt
  return !isLt && !isAutoRelationTableField(field)
}

const parentFieldIds = computed(() => (props.parentFields ?? []).map((f) => String(f.id)))

function isInherited(id: string | number) {
  return parentFieldIds.value.includes(String(id))
}

const isDirty = computed(() => !isEqual(draftFields.value, snapshot.value))
const isObjectTypeFieldsDirty = computed(
  () => !isEqual(objectTypeFieldDrafts.value, objectTypeFieldSnapshot.value),
)
const hasUnsavedChanges = computed(() =>
  isNewObjectTypeFieldsMode.value ? isObjectTypeFieldsDirty.value : isDirty.value,
)
const isQueryLoading = computed(
  () => isNewObjectTypeFieldsMode.value && props.objectTypeFieldsLoading,
)

function syncFromProps() {
  const src = (props.fields ?? []).filter(matchesMode)
  draftFields.value = cloneDeep(src)
  snapshot.value = cloneDeep(src)
}

/**
 * Синхронизирует локальный draft с ответом нового fields API.
 */
function syncObjectTypeFieldsFromQuery() {
  const drafts = filteredObjectTypeFields.value.map(objectTypeFieldToDraft)
  const sorted = [...drafts].sort((a, b) => {
    if (a.id == null) return 1
    if (b.id == null) return -1
    return a.id - b.id
  })
  objectTypeFieldDrafts.value = cloneDeep(sorted)
  objectTypeFieldSnapshot.value = cloneDeep(sorted)
}

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    if (isNewObjectTypeFieldsMode.value) {
      syncObjectTypeFieldsFromQuery()
      return
    }
    syncFromProps()
  },
)

watch(
  () => props.objectTypeFieldsResponse,
  () => {
    if (!props.modelValue || !isNewObjectTypeFieldsMode.value) return
    syncObjectTypeFieldsFromQuery()
  },
)

function close() {
  emit('update:modelValue', false)
}

function validate(): boolean {
  if (isNewObjectTypeFieldsMode.value) {
    return validateObjectTypeFields()
  }

  // Only validate editable (own) rows; inherited can be empty in edge cases and are read-only.
  const own = draftFields.value.filter((f) => !isInherited(f.id))
  for (const f of own) {
    const name = (f.name ?? '').trim()
    if (!name) {
      addError(trs('validate.field_name_required'))
      return false
    }
  }
  return true
}

/**
 * Проверяет обязательные поля новой формы собственных полей.
 */
function validateObjectTypeFields(): boolean {
  const typeIds = allowedFieldTypeIds.value

  for (const field of objectTypeFieldDrafts.value) {
    if (!field.name.trim()) {
      addError(trs('validate.field_name_required'))
      return false
    }

    if (!field.field_type || !typeIds.has(field.field_type)) {
      addError(trs('validate.field_type_required'))
      return false
    }

    if (isCatalogFieldsMode.value && !field.catalog_id) {
      addError(trs('validate.dictionary_required'))
      return false
    }
  }

  return true
}

/**
 * Добавляет локальный draft поля текущего класса.
 */
function addObjectTypeField() {
  const objectTypeId = objectTypeIdNumber.value
  if (!objectTypeId) {
    addError(trs('request.unknown_error'))
    return
  }

  const draft = createObjectTypeFieldDraft(
    objectTypeId,
    activeFieldTypes.value[0]?.id ?? null,
    objectTypeFieldDrafts.value.length + 1,
  )
  if (isCatalogFieldsMode.value) {
    draft.catalog_id = objectTypeFieldsResponse.value.dictionaries[0]?.id ?? null
  }

  objectTypeFieldDrafts.value.push(draft)
}

/**
 * Удаляет поле из локального draft до сохранения.
 */
function deleteObjectTypeField(localId: string) {
  const idx = objectTypeFieldDrafts.value.findIndex((item) => item.local_id === localId)
  if (idx !== -1) objectTypeFieldDrafts.value.splice(idx, 1)
}

/**
 * Сохраняет diff новой формы через POST/PUT/DELETE.
 */
async function persistObjectTypeFields(): Promise<boolean> {
  const objectTypeId = objectTypeIdNumber.value
  if (!objectTypeId) {
    addError(trs('request.unknown_error'))
    return false
  }
  if (!validateObjectTypeFields()) return false

  const normalizedDrafts = objectTypeFieldDrafts.value.map((field, index) => ({
    draft: field,
    payload: objectTypeFieldDraftToPayload(field, objectTypeId, index),
  }))
  const snapshotById = new Map(
    objectTypeFieldSnapshot.value
      .filter((field) => field.id != null)
      .map((field) => [field.id as number, field]),
  )
  const draftIds = new Set(
    objectTypeFieldDrafts.value
      .filter((field) => field.id != null)
      .map((field) => field.id as number),
  )

  for (const field of objectTypeFieldSnapshot.value) {
    if (field.id != null && !draftIds.has(field.id)) {
      await deleteObjectTypeFieldMutation.mutateAsync(field.id)
    }
  }

  for (const { draft, payload } of normalizedDrafts) {
    if (draft.id == null) {
      await createObjectTypeFieldMutation.mutateAsync(payload)
      continue
    }

    const previous = snapshotById.get(draft.id)
    const previousPayload = previous
      ? objectTypeFieldDraftToPayload(previous, objectTypeId, Math.max(previous.sort, 1) - 1)
      : null
    if (!previousPayload || !isEqual(payload, previousPayload)) {
      await updateObjectTypeFieldMutation.mutateAsync({
        fieldId: draft.id,
        payload,
      })
    }
  }

  const normalized = await props.refetchObjectTypeFields()
  const normalizedFieldTypes = isCatalogFieldsMode.value
    ? catalogFieldTypesFromClasses(normalized.fieldClasses)
    : primitiveFieldTypesFromClasses(normalized.fieldClasses)
  const normalizedFieldTypeIds = new Set(normalizedFieldTypes.map((item) => item.id))
  const normalizedFields = normalized.fields.filter((field) => {
    if (normalizedFieldTypeIds.has(field.field_type)) return true
    if (isCatalogFieldsMode.value) return Number(field.catalog_id) > 0
    return false
  })
  const drafts = normalizedFields.map(objectTypeFieldToDraft)
  objectTypeFieldDrafts.value = cloneDeep(drafts)
  objectTypeFieldSnapshot.value = cloneDeep(drafts)
  emit('fieldsSaved', objectTypeFieldsToInventoryFields(normalizedFields, normalizedFieldTypes))
  addSuccess(trs('itf.saved'))
  return true
}

async function persist() {
  if (!hasUnsavedChanges.value) {
    close()
    return
  }

  if (isNewObjectTypeFieldsMode.value) {
    saving.value = true
    try {
      const ok = await persistObjectTypeFields()
      if (ok) close()
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
    } finally {
      saving.value = false
    }
    return
  }

  if (!validate()) return

  saving.value = true
  try {
    // Merge back into the full fields list, keeping rows from other modes untouched.
    const next: ObjectInventoryField[] = []
    for (const f of props.fields ?? []) {
      if (matchesMode(f)) continue
      next.push(f)
    }
    next.push(...cloneDeep(draftFields.value))
    const ok = await props.persistFn(next)
    if (ok) close()
  } finally {
    saving.value = false
  }
}

function cancel() {
  close()
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    attach="body"
    :z-index="3400"
    max-width="1400"
    scrollable
    content-class="pb-shell-dialog"
    :retain-focus="false"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard class="pb-shell-modal__card" rounded="xl">
      <VToolbar density="compact" color="surface" class="pb-shell-modal__toolbar px-4 px-sm-6" flat>
        <VToolbarTitle class="text-h6 font-weight-bold">
          {{ title }}
        </VToolbarTitle>
        <VSpacer />
        <VBtn icon variant="text" @click="cancel">
          <VIcons type="close" />
        </VBtn>
      </VToolbar>

      <VCardText class="pb-shell-modal__body">
        <PassportBuilderFieldsModalBody
          v-model:fields="draftFields"
          v-model:object-type-fields="objectTypeFieldDrafts"
          :parent-fields="parentFields"
          :field-groups="fieldGroups"
          :mode="mode"
          :primitive-field-types="primitiveFieldTypes"
          :catalog-dictionaries="objectTypeFieldsResponse.dictionaries"
          :is-object-type-fields-loading="isQueryLoading"
          :is-object-type-fields-saving="saving"
          @add-object-type-field="addObjectTypeField"
          @delete-object-type-field="deleteObjectTypeField"
        />
      </VCardText>

      <VCardActions class="pb-shell-modal__actions px-4 px-sm-6 py-3">
        <VSpacer />
        <VBtn variant="text" :disabled="saving || isQueryLoading" @click="cancel">{{ $t('itf.cancel') }}</VBtn>
        <VBtn color="primary" :loading="saving" :disabled="saving || isQueryLoading" @click="persist">
          {{ $t('itf.save') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
/* Mirror PassportBuilderShellModal.vue — same card, toolbar, body scroll area. */
.pb-shell-modal__card {
  max-height: calc(100dvh - 32px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
}

.pb-shell-modal__toolbar {
  flex: none;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
}

.pb-shell-modal__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 16px !important;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  overflow-y: auto;
}

@media (min-width: 600px) {
  .pb-shell-modal__body {
    padding: 24px !important;
  }
}

/* Footer matches ListsModal */
.pb-shell-modal__actions {
  flex: none;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  background: rgb(var(--v-theme-surface));
  border-radius: 0 0 16px 16px;
  overflow: hidden;
}
</style>

<style lang="scss">
.pb-shell-dialog {
  padding: 16px;
  box-sizing: border-box;
}
</style>
